import React, { useCallback, useMemo, useState } from 'react';
import { isArray, isEqual, isNull, isUndefined } from 'lodash/fp';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../../../hooks/react-redux';
import { FewShotDocumentQuery, getFewShotQueries, getSavedFewShotDocument, MLFewShotDocument, setFewShotDocumentAnswer, setFewShotDocumentSelectedPage, updateFewShotAnswersStarted } from '../store';
import styles from './FewShotDocumentAnalysis.module.scss';
import { Scrollable } from '../../../../shared/scrollable/Scrollable';
import { Toggle } from '../../../../shared/toggle';
import { getAllDropdownLists } from '../../../dropdown-lists/store';
import { DatasetFieldType } from '../../../../datasets/store';
import { Dropdown, DropdownOption } from '../../../../shared/dropdown/Dropdown';
import { MLAnswer } from '../../../../datasets/instances/ml-modal/MLAnswer';
import { InformationTooltip } from '../../../../shared/tooltip';
import { Number } from '../../../../shared/number/Number';
import { Text } from '../../../../shared/text/Text';
import { SpaceBetweenButtonWrapper } from '../../../../shared/button/SpaceBetweenButtonWrapper';
import { Button } from '../../../../shared/button/Button';
import { IconButton } from '../../../../shared/button/IconButton';
import { DocumentHighlight, DocumentHighlightWithQuestionMark } from '../../../../shared/icons';
import { PDFPageHighlights } from '../../../../documents/document-viewer/store/types';
import { setIndexesToHighlight } from '../../../../documents/document-viewer/store/actions';
import { getIsCheckingForHighlights } from '../../../../documents/document-viewer/store';
import { AddressForNotice, MLDataValue } from '../../../../datasets/store/mlTypes';
import { AddressForNotices } from './AddressForNotices';

const { green } = styles;

interface FewShotQueriesProps {
    fewShotDocument: MLFewShotDocument;
}

export const FewShotQueries: React.FC<FewShotQueriesProps> = ({ fewShotDocument }) => {
    const [confirmedQueries, setConfirmedQueries] = useState<string[]>([]);

    const dispatch = useAppDispatch();

    const savedFewShotDocument = useAppSelector(getSavedFewShotDocument);
    const queries = useAppSelector(getFewShotQueries);
    const dropdownLists = useAppSelector(getAllDropdownLists);
    const checkingForHighlights = useAppSelector(getIsCheckingForHighlights);

    const documentHasUpdated = useMemo(() => !isEqual(fewShotDocument, savedFewShotDocument), [savedFewShotDocument, fewShotDocument]);

    const getIsConfirmedQuery = useCallback((key: string) => confirmedQueries.includes(key), [confirmedQueries]);

    const toggleConfirmQuery = useCallback((key: string) => {
        const isConfirmedQuery = getIsConfirmedQuery(key);
        const updatedConfirmedQueries = isConfirmedQuery ? confirmedQueries.filter(confirmedKey => confirmedKey !== key) : [...confirmedQueries, key];
        setConfirmedQueries(updatedConfirmedQueries);
    }, [getIsConfirmedQuery, confirmedQueries]);

    const confirm = useCallback(() => documentHasUpdated && dispatch(updateFewShotAnswersStarted()), [documentHasUpdated, dispatch]);

    const scrollToHighlightedAnswer = useCallback((page: number, highlightIndex: number) => {
        const answer = document.getElementById(`highlight-ref-${page}-${highlightIndex}`);
        if (!isNull(answer)) {
            answer.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
            dispatch(setFewShotDocumentSelectedPage(page));
        }
    }, [dispatch]);

    const highlightPdfAnswer = useCallback((value: PDFPageHighlights) => dispatch(setIndexesToHighlight(value)), [dispatch]);

    const highlightAnswer = useCallback((page: number, highlightIndexes?: number[]) => {
        if (highlightIndexes && highlightIndexes.length > 0) {
            highlightPdfAnswer({ page, highlightIndexes });
            setTimeout(() => scrollToHighlightedAnswer(page, highlightIndexes[0]), 1500);
        } else {
            const pageToScrollTo = document.getElementById(`pdf-page-${page}`);
            if (!isNull(pageToScrollTo)) {
                pageToScrollTo.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [highlightPdfAnswer, scrollToHighlightedAnswer]);

    const getDropdownOptions = useCallback((id: number | null) => (dropdownLists.find(({ dropdownListId }) => dropdownListId === id)?.options || []).map(value => ({ value, label: value })), [dropdownLists]);

    const getCurrentFewShotAnswer = useCallback((key: string) => {
        if (!isNull(fewShotDocument.fewShotAnswers)) {
            const foundMLAnswer = Object.entries(fewShotDocument.fewShotAnswers).find(([queryKey]) => queryKey === key);
            if (foundMLAnswer) {
                return foundMLAnswer[1];
            }
        }
        return { value: '' };
    }, [fewShotDocument.fewShotAnswers]);

    const updateAnswer = useCallback((key: string, value: MLDataValue) => dispatch(setFewShotDocumentAnswer(key, value)), [dispatch]);

    const updateDropdownAnswer = useCallback((key: string, dropdownValue: DropdownOption | Options<DropdownOption> | null, isMulti: boolean) => {
        let value: string | string[] | null = null;
        if (!isNull(dropdownValue)) {
            value = isMulti ? (dropdownValue as DropdownOption[]).map(({ value }) => value) : (dropdownValue as DropdownOption).value;
        }
        updateAnswer(key, value);
    }, [updateAnswer]);

    const getExpectedQueryResponse = useCallback((query: FewShotDocumentQuery) => {
        const currentValue = getCurrentFewShotAnswer(query.key).value;
        if (query.isEdgeCase && ['partyANoticesAddress', 'partyBNoticesAddress'].includes(query.systemId) && !isNull(currentValue) && !isUndefined(currentValue)) {
            const addressForNoticeValue = currentValue as AddressForNotice[];
            return (
                <AddressForNotices value={addressForNoticeValue} updateValue={value => updateAnswer(query.key, value)} />
            );
        }
        if (query.isEdgeCase) {
            return (
                <div className={styles.edgeCaseWrapper}>
                    <InformationTooltip content='We are not currently allowing edge case responses to be edited' />
                    <div className={styles.edgeCaseAnswer}><MLAnswer answer={currentValue} depth={0} /></div>
                </div>
            );
        }
        if (query.fieldType === DatasetFieldType.DROPDOWN) {
            const valueAsDropdown = currentValue as string | string[] | null;
            const dropdownOptions = getDropdownOptions(query.dropdownListId);
            const dropdownValue = !isNull(valueAsDropdown) && dropdownOptions.filter(({ value }) => typeof valueAsDropdown === 'string' ? value === valueAsDropdown : valueAsDropdown.includes(value)) || null;
            const placeholder = isNull(valueAsDropdown) || valueAsDropdown === '' || (isArray(valueAsDropdown) && valueAsDropdown.every(value => value === '')) ? 'No value was found by AI' : 'AI found an answer that was not in this dropdown list';
            return (
                <Dropdown
                    options={dropdownOptions}
                    value={dropdownValue}
                    onChange={dropdownValue => updateDropdownAnswer(query.key, dropdownValue, query.isMultiSelect)}
                    placeholder={placeholder}
                    isMulti={query.isMultiSelect}
                />
            );
        }
        if (query.fieldType === DatasetFieldType.NUMBER) {
            const numberValue = (isUndefined(currentValue) || isNull(currentValue) || typeof currentValue === 'number') ? currentValue : undefined;
            return (
                <Number value={numberValue} onChange={value => updateAnswer(query.key, value || null)} />
            );
        }
        const stringValue = (isUndefined(currentValue) || typeof currentValue === 'string') ? currentValue : '';
        return (
            <Text value={stringValue} onChange={e => updateAnswer(query.key, e.currentTarget.value)} testId={`few-shot-query-text-${query.key}`} />
        );
    }, [getCurrentFewShotAnswer, getDropdownOptions, updateAnswer, updateDropdownAnswer]);

    return (
        <div className={styles.fewShotQueriesWrapper}>
            <div className={styles.fewShotQueriesHeader}>Update and Confirm Few Shot Answers</div>
            <div className={styles.fewShotQueries}>
                <Scrollable>
                    {queries.map(query => {
                        const { question, key } = query;
                        const isConfirmedQuery = getIsConfirmedQuery(key);
                        const fewShotResponse = getCurrentFewShotAnswer(key);
                        const showPdfHighlight = !isUndefined(fewShotResponse.page);
                        const highlightIcon = !checkingForHighlights && isUndefined(fewShotResponse.highlightIndexes) || fewShotResponse.isFuzzyMatchHighlight ? DocumentHighlightWithQuestionMark : DocumentHighlight;
                        return (
                            <div className={styles.queryWrapper} key={key}>
                                {showPdfHighlight && <div className={styles.queryHighlight}><IconButton icon={highlightIcon} showSpinner={checkingForHighlights} onClick={() => highlightAnswer(fewShotResponse.page!, fewShotResponse.highlightIndexes)} /></div>}
                                <div className={styles.query}>{question}</div>
                                <div className={styles.expectedResponseWrapper}>{getExpectedQueryResponse(query)}</div>
                                <div className={styles.queryConfirmWrapper}><Toggle checked={isConfirmedQuery} onChange={() => toggleConfirmQuery(key)} onColour={green} /></div>
                            </div>
                        );
                    })}
                </Scrollable>
            </div>
            <SpaceBetweenButtonWrapper leftButtons={[]} rightButtons={[<Button key='confirm' label='Confirm' onClick={confirm} disabled={!documentHasUpdated} />]} />
        </div>
    );
};
